import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ActionSheetController, ModalController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { DownloadOrderInsightsEvent } from '@order/models/download-orders-event';
import { OrderInsightsService } from '@order/services/order-insights.service';
import { OrderAnalyticsService } from '@order/tracking/analytics/order-analytics.service';
import { PanAppState, PlatformService } from '@panamax/app-state';
import { Crumb } from '@shared/components/breadcrumbs/breadcrumbs.component';
import { DownloadFileTypes } from '@shared/constants/order-download-file';
import { PageTitle } from '@shared/constants/page-titles.enum';
import { NavigationHelperService } from '@shared/helpers/navigation.helpers.service';
import { Product } from '@shared/models/product.model';
import { LoadingSpinnerService } from '@shared/services/loading-spinner/loading-spinner.service';
import { QuantityUpdate } from '@shared/models/quantity-update';
import { OrderItemService } from '@shared/services/order-item.service';
import { Order } from '@usf/ngrx-order';
import { BehaviorSubject, filter, Observable, take, tap } from 'rxjs';
import { OrderService } from '@order/services/order.service';
import { OrderViewModel } from '@order/models/order-view-model';
import { FEATURES } from '@shared/constants/splitio-features';
import {
  OrderPercentage,
  OrderPercentagesDisplayed,
} from '@shared/models/percentages-displayed-model';
import {
  InsightsProducts,
  InsightsProductsFilter,
} from '@order/pages/order-insights/models/insights-products.model';
import { OrderInsightsPageHeaderService } from '@order/pages/order-insights/service/order-insights-page-header.service';

@Component({
  selector: 'app-order-insights',
  templateUrl: './order-insights.page.html',
  styleUrls: ['./order-insights.page.scss'],
})
export class OrderInsightsPage implements OnInit {
  breadcrumbs: Crumb[];
  order: Order;
  productMap: Map<number, Product>;
  lastOrderedDates: {
    [key: number]: string | Date;
  };
  percentagesDisplayed: OrderPercentagesDisplayed = {
    percentages: [],
    lineDivider: false,
  };
  downloadModalIsOpen$ = new BehaviorSubject<boolean>(false);
  downloadFileTypes = [DownloadFileTypes.CSV];
  cartOrderVm$: Observable<OrderViewModel>;
  cartOrderVm: OrderViewModel;
  quickFilters: InsightsProductsFilter[] = [];
  productsDisplayed: InsightsProducts[] = [];
  lostLinesProducts: InsightsProducts;
  newProductsOrderedProducts: InsightsProducts;
  productsInOrder: InsightsProducts;

  readonly dynamicCookbookOrderFeatureFlag =
    FEATURES.split_dynamic_order_cookbook.name;
  dynamicOrderInsightsTabsFeatureFlag$: Observable<boolean>;

  constructor(
    readonly platformService: PlatformService,
    private orderInsightsService: OrderInsightsService,
    private orderAnalyticsService: OrderAnalyticsService,
    private navigationHelperService: NavigationHelperService,
    private loadingSpinnerService: LoadingSpinnerService,
    private translateService: TranslateService,
    public modalController: ModalController,
    public actionSheetController: ActionSheetController,
    private route: ActivatedRoute,
    private orderItemService: OrderItemService,
    private orderService: OrderService,
    readonly panAppState: PanAppState,
    private orderInsightsPageHeaderService: OrderInsightsPageHeaderService,
  ) {}

  ngOnInit(): void {
    this.cartOrderVm$ = this.getCurrentOrder$().pipe(
      tap(cartOrderVm => (this.cartOrderVm = cartOrderVm)),
    );
    this.dynamicOrderInsightsTabsFeatureFlag$ = this.panAppState.feature$([
      FEATURES.split_dynamic_order_partial_exceptions.name,
    ]);
  }

  ionViewWillEnter() {
    const orderId = this.route.snapshot.paramMap.get('orderId');
    this.populateBreadcrumbs();
    this.loadOrderAndProductData(orderId);
  }

  loadPercentagesDisplayed() {
    this.percentagesDisplayed.lineDivider = true;
    this.percentagesDisplayed.percentages = [
      {
        percentage: this.order?.orderHeader?.cookbookAdoptionPercent,
        title: this.translateService.instant(
          'i18n.orderInsightsPage.cookbookAdoptionTitle',
        ),
        displayFlag: this.dynamicCookbookOrderFeatureFlag,
        dataCy: 'cookbook-adoption',
      } as OrderPercentage,
    ] as OrderPercentage[];
    this.percentagesDisplayed.percentages =
      this.percentagesDisplayed.percentages.filter(item => item?.percentage);
  }

  loadProductInsightTabs() {
    this.lostLinesProducts =
      this.orderInsightsPageHeaderService.getLostLinesProducts(
        this.order,
        this.productMap,
      );
    this.newProductsOrderedProducts =
      this.orderInsightsPageHeaderService.getNewProductsOrderedProducts(
        this.order,
        this.productMap,
      );
    this.productsInOrder =
      this.orderInsightsPageHeaderService.getProductsInOrder(
        this.order,
        this.productMap,
      );
    this.quickFilters = this.orderInsightsPageHeaderService.getQuickFilters(
      this.lostLinesProducts,
      this.newProductsOrderedProducts,
      this.productsInOrder,
    );
    // Set initial products to display
    this.productsDisplayed = this.quickFilters[0].insightsProducts;
  }

  selectTab(tab: InsightsProductsFilter): void {
    this.quickFilters.forEach(filter => {
      filter.selected = tab.id === filter.id;
    });
    this.productsDisplayed = tab.insightsProducts;
  }

  loadOrderAndProductData(orderId) {
    this.loadingSpinnerService.createSpinnerModal().then(() => {
      this.orderInsightsService
        .loadOrderAndProductData$(orderId)
        .pipe(take(1))
        .subscribe(([order, productMap, lastOrdered]) => {
          this.loadingSpinnerService.dismissSpinnerModal();

          this.order = order;
          this.productMap = productMap;
          this.lastOrderedDates = lastOrdered;
          this.loadPercentagesDisplayed();
          this.loadProductInsightTabs();

          this.orderAnalyticsService.trackOrderInsightsPageLoad(
            productMap,
            order?.orderHeader,
          );
          this.panAppState.setPageTitle(
            this.translateService.instant(PageTitle.orderInsights) +
              ` (${
                order?.orderHeader?.dyfProductsNotOrdered?.length +
                order?.orderHeader?.newProductsOrderedNotInRecentlyPurchased
                  ?.length
              })`,
          );
          if (
            !!order?.orderHeader?.tandemOrderNumber &&
            !!this.breadcrumbs?.length
          ) {
            this.breadcrumbs.splice(2, 0, {
              label:
                this.translateService.instant('i18n.breadcrumbs.orderNumber') +
                `${order?.orderHeader?.tandemOrderNumber}`,
              url: `/desktop/order/submitted-order/${orderId}`,
              active: false,
            });
          }
        });
    });
  }

  getOrderedQuantityString(productNumber: number): string {
    return this.orderInsightsService.getOrderedQuantityString(
      this.order?.orderItems,
      productNumber,
    );
  }

  routeToProductDetails(productNumber: number) {
    this.navigationHelperService.routeToProductDetails(productNumber);
  }

  showDownloadInsightsModal(isOpen: boolean) {
    this.downloadModalIsOpen$.next(isOpen);
  }

  handleDownloadInsights(event) {
    const insightInformation = this.orderInsightsService.getOrderInsightDataMap(
      this.order,
      this.productMap,
      this.lastOrderedDates,
    );

    const downloadEvent: DownloadOrderInsightsEvent = {
      fileName: event.fileName,
      fileType: DownloadFileTypes.CSV,
      orderNumber: this.order?.orderHeader?.tandemOrderNumber,
      insightInformation,
    };
    this.orderInsightsService.downloadOrderInsights(downloadEvent);
  }

  async openAdditionalOptions(e: Event) {
    let buttons = [
      {
        text: this.translateService.instant('i18n.orderPage.download'),
        handler: () => {
          this.showDownloadInsightsModal(true);
        },
      },
      {
        text: this.translateService.instant('i18n.common.cancel'),
        role: 'cancel',
        cssClass: 'cancel-button',
      },
    ];

    const additionalOptionsActionSheet =
      await this.actionSheetController.create({
        cssClass: 'action-sheet-touch',
        mode: 'ios',
        buttons,
      });
    await additionalOptionsActionSheet.present();
  }

  populateBreadcrumbs() {
    this.breadcrumbs = [
      {
        label: 'i18n.breadcrumbs.home',
        url: '/desktop/home',
        active: false,
      },
      {
        label: 'i18n.breadcrumbs.myOrders',
        url: '/desktop/my-orders-view',
        active: false,
      },
      {
        label: 'i18n.breadcrumbs.orderInsights',
        url: '',
        active: true,
      },
    ];
  }

  getCurrentOrder$(): Observable<OrderViewModel> {
    return this.orderService.loadOrderViewModel$().pipe(filter(vm => !!vm));
  }

  async changeQuantityHandler(quantityUpdate: QuantityUpdate) {
    this.orderItemService.changeQuantity(
      quantityUpdate.productNumber,
      quantityUpdate.quantity,
      quantityUpdate.isEaches,
    );
    this.orderAnalyticsService.trackOrderInsightAddToOrder(
      this.cartOrderVm?.orderHeader,
      quantityUpdate.quantity,
      quantityUpdate.isEaches,
      quantityUpdate.productNumber,
      this.cartOrderVm?.productsMap,
    );
  }
}
